import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { EquipmentInfo } from '../components/equipment/EquipmentInfo';
import { RentalBookingForm } from '../components/equipment/RentalBookingForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EquipmentDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();

  const [equipment, setEquipment] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);

  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    rental_type: 'daily',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    selected_attachment: '',
  });

  const [pricing, setPricing] = useState({
    days: 0,
    rental_amount: 0,
    deposit: 0,
    add_ons_amount: 0,
    total: 0,
  });

  const fetchEquipment = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/equipment/${id}`);
      setEquipment(response.data);
    } catch (error) {
      toast({
        title: 'Error loading equipment',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/equipment?category=Tractor Attachments`);
      setAttachments(response.data.equipment || []);
    } catch (error) {
      // optional
    }
  }, []);

  useEffect(() => {
    fetchEquipment();
    fetchAttachments();
  }, [fetchEquipment, fetchAttachments]);

  const calculatePricing = useCallback(() => {
    if (!equipment || !formData.start_date || !formData.end_date) {
      setPricing({ days: 0, rental_amount: 0, deposit: 0, add_ons_amount: 0, total: 0 });
      return;
    }

    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));

    let rental_amount = 0;
    if (formData.rental_type === 'weekly') {
      const weeks = Math.floor(days / 7);
      const remaining = days % 7;
      rental_amount = weeks * equipment.weekly_rate + remaining * equipment.daily_rate;
    } else {
      rental_amount = days * equipment.daily_rate;
    }

    setPricing({
      days,
      rental_amount,
      deposit: equipment.deposit,
      add_ons_amount: 0,
      total: rental_amount + equipment.deposit,
    });
  }, [equipment, formData.start_date, formData.end_date, formData.rental_type]);

  useEffect(() => {
    calculatePricing();
  }, [calculatePricing]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.start_date || !formData.end_date) {
      toast({ title: 'Missing dates', description: 'Please select rental dates', variant: 'destructive' });
      return;
    }

    if (equipment.category === 'Tractors' && !formData.selected_attachment) {
      toast({ title: 'Select attachment', description: 'Please choose a tractor attachment', variant: 'destructive' });
      return;
    }

    if (!smsConsent) {
      toast({
        title: 'SMS consent required',
        description: 'Please agree to receive rental-related text messages.',
        variant: 'destructive',
      });
      return;
    }

    setBookingLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/equipment/rentals/create-checkout`, {
        equipment_id: equipment.id,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        rental_type: formData.rental_type,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        tractor_attachment_id: formData.selected_attachment || null,
        add_ons: [],
        sms_consent: smsConsent,
        origin_url: window.location.origin,
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast({
        title: 'Booking failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#050814] flex items-center justify-center">
        <p className="text-lg text-[#F4F1EA]/70">Loading equipment...</p>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="min-h-screen pt-20 bg-[#050814] flex items-center justify-center">
        <p className="text-lg text-[#F4F1EA]/70">Equipment not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <EquipmentInfo equipment={equipment} />
          <RentalBookingForm
            equipment={equipment}
            attachments={attachments}
            formData={formData}
            pricing={pricing}
            bookingLoading={bookingLoading}
            smsConsent={smsConsent}
            onSmsConsentChange={setSmsConsent}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};
